import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from "pdfjs-dist"
import { createPdfResources, PdfResourceError } from "../lib/pdf_resources"

// PDF.js viewer controller for inline document viewing (Issue #822)
//
// Uses PDF.js library to render PDF documents in a canvas element
// with navigation and zoom controls.
//
// @example HTML usage
//   <div data-controller="pdf-viewer"
//        data-pdf-viewer-url-value="/path/to/document.pdf">
//     <button data-action="pdf-viewer#previousPage">Previous</button>
//     <span data-pdf-viewer-target="pageIndicator">Page 1 of 1</span>
//     <button data-action="pdf-viewer#nextPage">Next</button>
//     <button data-action="pdf-viewer#zoomIn">Zoom In</button>
//     <button data-action="pdf-viewer#zoomOut">Zoom Out</button>
//     <canvas data-pdf-viewer-target="pdfCanvas"></canvas>
//   </div>
export default class extends Controller {
  static values = {
    url: String,
    workerSrc: String,
    resourcesUrl: String
  }

  static targets = [
    "pdfCanvas",
    "pageIndicator",
    "prevButton",
    "nextButton",
    "zoomInButton",
    "zoomOutButton",
    "downloadLink",
    "canvas"
  ]

  // Initialize controller state
  connect() {
    this.generation = (this.generation || 0) + 1
    this.connected = true
    this.failed = false
    this.isLoading = false
    this.pdfDoc = null
    this.currentPage = 1
    this.totalPages = 0
    this.scale = 1.0
    this.minScale = 0.5
    this.maxScale = 3.0
    this.scaleStep = 0.25

    // The helper supplies Propshaft's fingerprint-aware URL for the worker that
    // esbuild emits from this same installed pdfjs-dist package (#3009). Keeping
    // both entry points in one package prevents API/worker version drift.
    if (!this.hasWorkerSrcValue || !this.hasResourcesUrlValue) {
      console.error("PDF.js worker source is missing")
      this.showError("Failed to load PDF viewer")
      return
    }
    pdfjsLib.GlobalWorkerOptions.workerSrc = this.workerSrcValue

    if (this.urlValue) {
      this.loadPdf()
    }
  }

  // Load PDF document from URL
  async loadPdf() {
    this.releaseResources()
    const generation = ++this.generation
    this.failed = false
    this.isLoading = true
    this.resourceAbortController = new AbortController()
    const signal = this.resourceAbortController.signal
    if (this.hasPageIndicatorTarget) this.pageIndicatorTarget.textContent = "Loading PDF…"
    try {
      const resources = await createPdfResources({
        url: this.resourcesUrlValue, version: pdfjsLib.version, signal,
        onError: error => this.fail(error, generation)
      })
      if (!this.current(generation)) return
      this.loadingTask = pdfjsLib.getDocument({ url: this.urlValue, ...resources, stopAtErrors: true })
      const document = await this.loadingTask.promise
      if (!this.current(generation)) return
      this.pdfDoc = document
      this.totalPages = this.pdfDoc.numPages
      this.updatePageIndicator()
      await this.renderPage(this.currentPage)
      if (this.current(generation)) this.isLoading = false
    } catch (error) {
      if (!signal.aborted) this.fail(error, generation)
    }
  }

  // Render a specific page
  async renderPage(pageNumber) {
    if (!this.pdfDoc || this.failed) return
    const generation = this.generation
    const renderGeneration = this.renderGeneration = (this.renderGeneration || 0) + 1
    this.renderTask?.cancel()

    try {
      const page = await this.pdfDoc.getPage(pageNumber)
      if (!this.current(generation) || renderGeneration !== this.renderGeneration) return
      const viewport = page.getViewport({ scale: this.scale })

      const canvas = this.pdfCanvasTarget
      const context = canvas.getContext("2d")

      canvas.height = viewport.height
      canvas.width = viewport.width

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      }

      const task = this.renderTask = page.render(renderContext)
      await task.promise
      if (this.current(generation) && renderGeneration === this.renderGeneration) this.updateNavigationState()
    } catch (error) {
      if (error?.name !== "RenderingCancelledException" && renderGeneration === this.renderGeneration) {
        this.fail(error, generation)
      }
    }
  }

  // Navigate to previous page
  previousPage() {
    if (this.failed) return
    if (this.currentPage <= 1) return

    this.currentPage--
    this.updatePageIndicator()
    this.renderPage(this.currentPage)
  }

  // Navigate to next page
  nextPage() {
    if (this.failed) return
    if (this.currentPage >= this.totalPages) return

    this.currentPage++
    this.updatePageIndicator()
    this.renderPage(this.currentPage)
  }

  // Increase zoom level
  zoomIn() {
    if (this.failed) return
    if (this.scale >= this.maxScale) return

    this.scale = Math.min(this.scale + this.scaleStep, this.maxScale)
    this.renderPage(this.currentPage)
  }

  // Decrease zoom level
  zoomOut() {
    if (this.failed) return
    if (this.scale <= this.minScale) return

    this.scale = Math.max(this.scale - this.scaleStep, this.minScale)
    this.renderPage(this.currentPage)
  }

  // Update page indicator text
  updatePageIndicator() {
    if (this.hasPageIndicatorTarget) {
      this.pageIndicatorTarget.textContent = `Page ${this.currentPage} of ${this.totalPages}`
    }
  }

  // Update navigation button states
  updateNavigationState() {
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = this.currentPage <= 1
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = this.currentPage >= this.totalPages
    }
    if (this.hasZoomInButtonTarget) {
      this.zoomInButtonTarget.disabled = this.scale >= this.maxScale
    }
    if (this.hasZoomOutButtonTarget) {
      this.zoomOutButtonTarget.disabled = this.scale <= this.minScale
    }
  }

  // Display error message in canvas container
  showError(message) {
    if (this.hasCanvasTarget) {
      this.canvasTarget.innerHTML = `<div class="pdf-error alert alert-danger">${message}</div>`
    }
  }

  current(generation) {
    return this.connected && !this.failed && this.generation === generation
  }

  fail(error, generation) {
    if (!this.current(generation)) return
    this.failed = true
    this.isLoading = false
    this.lastError = error
    this.showError(error instanceof PdfResourceError ? "Failed to load PDF resources" : "Failed to load PDF document")
    if (this.hasPageIndicatorTarget) this.pageIndicatorTarget.textContent = "PDF unavailable"
    for (const name of ["Prev", "Next", "ZoomIn", "ZoomOut"]) {
      if (this[`has${name}ButtonTarget`]) this[`${name[0].toLowerCase()}${name.slice(1)}ButtonTarget`].disabled = true
    }
    this.releaseResources()
  }

  releaseResources() {
    this.renderTask?.cancel()
    this.renderTask = null
    this.resourceAbortController?.abort()
    this.resourceAbortController = null
    const owned = this.loadingTask || this.pdfDoc
    this.loadingTask = null
    this.pdfDoc = null
    // PDF.js owns worker termination through the loading task, including a load
    // that has not resolved. Cancellation rejection must not become unhandled.
    return Promise.resolve(owned?.destroy()).catch(() => {})
  }

  disconnect() {
    this.connected = false
    this.isLoading = false
    this.generation++
    return this.releaseResources()
  }
}
